import { useCallback, useSyncExternalStore } from 'react';

/**
 * 訂閱一個 CSS media query 的比對結果。
 *
 * 伺服器／靜態產生階段一律回傳 `false`：這些 app 是靜態輸出，HTML 產於建置時，
 * 當下無從得知使用者的裝置，先猜一個值會造成 hydration 不一致。
 * 因此請把 `false` 當成「尚未確定」，用它決定**加不加**行為（如加上手勢），
 * 而不是用來二選一切換版面——後者會在掛載瞬間閃一下。
 *
 * 以 `useSyncExternalStore` 實作而非 `useEffect` + `setState`：matchMedia 本來就是
 * 一個外部可訂閱源，用這支 hook 才能讓 React 在 render 階段就讀到正確值，
 * 不會多一次「先 false 再更新」的重繪。
 *
 * @example
 * const isTouch = useMediaQuery('(pointer: coarse)');
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const mediaQuery = window.matchMedia(query);
      mediaQuery.addEventListener('change', onStoreChange);

      return () => mediaQuery.removeEventListener('change', onStoreChange);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    /* server snapshot：靜態產生時沒有 window，也無從得知裝置 */
    () => false,
  );
}
